import type { QueueNameValue } from '@steado/admin-contract/jobs';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useState } from 'react';

import { ConfirmDialog } from '@/components/shell/ConfirmDialog';
import { Drawer } from '@/components/shell/Drawer';
import { BadgeCell } from '@/components/table/cells';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/lib/auth/auth-context';
import { Field, Fields, Panel, TabSkeleton } from '@/components/panel/panels';
import { TabError } from '@/screens/user-detail/TabError';
import { formatDateTime } from '@/lib/format';

import { JOB_STATE_TONES } from './job-tone';
import { discardJob, jobQueryOptions, retryJob } from './jobs-query';

interface JobDrawerProps {
  readonly queue: QueueNameValue;
  readonly jobId: string;
  readonly onClose: () => void;
}

export function JobDrawer({ queue, jobId, onClose }: JobDrawerProps) {
  const { api, can } = useAuth();
  const queryClient = useQueryClient();
  const query = useQuery(jobQueryOptions(api, queue, jobId));

  const [isDiscarding, setDiscarding] = useState(false);

  const invalidateLists = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: ['admin', 'jobs'] });
    void queryClient.invalidateQueries({ queryKey: ['admin', 'queues'] });
  }, [queryClient]);

  const retry = useMutation({
    mutationFn: () => retryJob(api, queue, jobId),
    onSuccess: (job) => {
      queryClient.setQueryData(['admin', 'job', queue, jobId], job);
      invalidateLists();
    },
    retry: false,
  });

  const discard = useMutation({
    mutationFn: () => discardJob(api, queue, jobId),
    onSuccess: () => {
      // The job no longer exists, so its cache entry must go rather than sit
      // there answering with a row Redis has forgotten.
      queryClient.removeQueries({ queryKey: ['admin', 'job', queue, jobId] });
      invalidateLists();
      setDiscarding(false);
      onClose();
    },
    retry: false,
  });

  const job = query.data;
  const disabledTitle = can.mutate ? undefined : 'Acting on a job requires the admin role.';

  return (
    <>
      <Drawer
        title={job === undefined ? 'Job' : `${job.name} · ${job.id}`}
        subtitle={queue}
        onClose={onClose}
        footer={
          job === undefined ? undefined : (
            <>
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  retry.mutate();
                }}
                // Retry is meaningful only from `failed`; the server refuses it
                // anyway, and offering it elsewhere is an invitation to a 422.
                disabled={!can.mutate || job.state !== 'failed' || retry.isPending}
                title={job.state === 'failed' ? disabledTitle : 'Only a failed job can be retried.'}
              >
                {retry.isPending ? 'Retrying…' : 'Retry'}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setDiscarding(true);
                }}
                disabled={!can.mutate || discard.isPending}
                title={disabledTitle}
                className="border-err-fg/30 text-err-fg hover:bg-err-bg hover:text-err-fg"
              >
                Discard…
              </Button>
            </>
          )
        }
      >
        {query.isPending ? <TabSkeleton rows={4} /> : null}
        {query.isError ? (
          <TabError message={query.error.message} onRetry={() => void query.refetch()} />
        ) : null}

        {job === undefined ? null : (
          <div className="flex flex-col gap-5">
            <div className="flex flex-wrap items-center gap-2">
              <BadgeCell label={job.state} tone={JOB_STATE_TONES[job.state]} dot />
              <BadgeCell
                label={`attempt ${String(job.attemptsMade)}/${String(job.maxAttempts)}`}
                tone={job.attemptsMade >= job.maxAttempts ? 'err' : 'mute'}
              />
            </div>

            {job.failedReason === null ? null : (
              <Panel title="Failure">
                <p className="font-mono text-[13px] leading-relaxed break-words text-err-fg">
                  {job.failedReason}
                </p>
              </Panel>
            )}

            <Panel title="Timing">
              <Fields>
                <Field label="Enqueued" value={formatDateTime(job.createdAt)} />
                <Field
                  label="Started"
                  value={job.processedAt === null ? null : formatDateTime(job.processedAt)}
                />
                <Field
                  label="Finished"
                  value={job.finishedAt === null ? null : formatDateTime(job.finishedAt)}
                />
                <Field label="Delay" value={job.delayMs === 0 ? 'none' : `${String(job.delayMs)} ms`} />
              </Fields>
            </Panel>

            <Panel title="Payload">
              <pre className="overflow-x-auto rounded-md bg-muted p-3 font-mono text-xs leading-relaxed text-foreground">
                {JSON.stringify(job.data, null, 2)}
              </pre>
              <p className="mt-3 text-[13px] leading-relaxed text-muted-foreground">
                {/* The elision happens on the server, not here — the large
                    values were never sent. */}
                Large values are elided by the API before they are sent. A food-photo job’s image never
                reaches this console.
              </p>
            </Panel>

            {job.stacktrace.length === 0 ? null : (
              <Panel title={`Stack (${String(job.stacktrace.length)})`}>
                <ol className="flex flex-col gap-2">
                  {job.stacktrace.map((frame, index) => (
                    <li key={`${String(index)}-${frame.slice(0, 24)}`}>
                      <pre className="overflow-x-auto rounded-md bg-muted p-3 font-mono text-xs leading-relaxed text-muted-foreground">
                        {frame}
                      </pre>
                    </li>
                  ))}
                </ol>
              </Panel>
            )}

            {retry.isError ? (
              <Alert role="alert" tone="err">
                <AlertDescription>{retry.error.message}</AlertDescription>
              </Alert>
            ) : null}
          </div>
        )}
      </Drawer>

      {isDiscarding && job !== undefined ? (
        <ConfirmDialog
          title={`Discard job ${job.id}?`}
          body={
            <div className="flex flex-col gap-2">
              <p>
                The job leaves Redis and does not come back. This is the only action in the console with no
                undo.
              </p>
              <p>
                Its payload and failure reason are written to the audit log first. Afterwards that row is all
                that is left of it.
              </p>
            </div>
          }
          confirmLabel="Discard"
          destructive
          // The one place the typed confirmation is used. Everything else in
          // the console is reversible, and a speed bump everywhere teaches
          // people to drive through them.
          typeToConfirm={job.id}
          isPending={discard.isPending}
          {...(discard.isError ? { error: discard.error.message } : {})}
          onConfirm={() => {
            discard.mutate();
          }}
          onCancel={() => {
            setDiscarding(false);
          }}
        />
      ) : null}
    </>
  );
}
